import { createContext, useContext } from 'react'
import axios from 'axios'
import { config } from '../../config'

export interface MainUser {
    id: number
    email: string
    name: string
    permissions: string
    status?: string
    isPremium?: boolean
    subscriptionEndDate?: string
    avatarUrl?: string
    subscription_status?: string
    verified?: boolean
    has2FA?: boolean
}

export interface RegisterResponse {
    user: {
        id: number;
        username: string;
        email: string;
        full_name: string;
        verified: boolean;
        message?: string;
    }
    emailDeliveryFailed?: boolean;
    error?: string;
    skipVerification?: boolean;
    redirectTo?: string;
    token?: string;
    cmsToken?: string;
    message?: string;
}

export interface LoginResult {
    success: boolean;
    error?: string | undefined;
    needsVerification?: boolean;
    requires2FA?: boolean;
    requiresEmailVerification?: boolean;
    email?: string | undefined;
    userId?: number | undefined;
    sessionToken?: string | undefined;
    authMethodPreference?: string | undefined;
}

interface AuthContextMainType {
    user: MainUser | null
    login: (credentials: { email: string; password: string }) => Promise<LoginResult>
    logout: () => Promise<void>
    isAuthenticated: boolean
    register: (data: { username: string; email: string; password: string; full_name: string }) => Promise<RegisterResponse>
    refreshUser: () => Promise<void>
    isAdmin: boolean
    checkAdminStatus: () => Promise<boolean>
    verifyEmail: (data: { email: string; code: string }) => Promise<{ success: boolean; error?: string }>
    resendVerification: (email: string) => Promise<{ success: boolean; error?: string }>
    // forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>
    // resetPassword: (data: { token: string; password: string }) => Promise<{ success: boolean; error?: string }>
    // setup2FA: () => Promise<{ secret: string; qrCodeUrl: string; error?: string }>
    // verify2FASetup: (data: { secret: string; token: string }) => Promise<{ success: boolean; backupCodes?: string[]; error?: string }>
    // disable2FA: (token: string) => Promise<{ success: boolean; error?: string }>
}

export const AuthContextMain = createContext<AuthContextMainType | null>(null)

export const useAuthMain = () => {
    const context = useContext(AuthContextMain)
    if (!context) {
        throw new Error('useAuthMain must be used within an AuthProviderMain')
    }
    return context
}

export const register = async (data: { username: string; email: string; password: string; full_name: string }) => {
    try {
        const response = await axios.post(
            `${config.server}/main/register`,
            data,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        return response.data;
    } catch (error: unknown) {
        const responseError = error as {
            response?: {
                data?: {
                    error?: string;
                    emailDeliveryFailed?: boolean;
                }
            }
        };

        throw new Error(
            (responseError && 'response' in responseError && responseError.response?.data?.error) ||
            'Registration failed'
        );
    }
}

export const forgotPassword = async (email: string) => {
    try {
        const response = await axios.post(
            `${config.server}/main/forgot-password`,
            { email },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        return { success: true, ...response.data };
    } catch (error: unknown) {
        const responseError = error as {
            response?: {
                data?: {
                    error?: string;
                }
            }
        };

        return {
            success: false,
            error: (responseError && 'response' in responseError && responseError.response?.data?.error) ||
                'Failed to process password reset request'
        };
    }
}

export const resetPassword = async (data: { token: string; password: string }) => {
    try {
        const response = await axios.post(
            `${config.server}/main/reset-password`,
            data,
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        return { success: true, ...response.data };
    } catch (error: unknown) {
        const responseError = error as {
            response?: {
                data?: {
                    error?: string;
                }
            }
        };

        return {
            success: false,
            error: (responseError && 'response' in responseError && responseError.response?.data?.error) ||
                'Failed to reset password'
        };
    }
}

export const setup2FA = async () => {
    try {
        const response = await axios.post(
            `${config.server}/main/generate-2fa-secret`,
            {},
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        return response.data;
    } catch (error: unknown) {
        const responseError = error as {
            response?: {
                data?: {
                    error?: string;
                }
            }
        };

        return {
            error: (responseError && 'response' in responseError && responseError.response?.data?.error) ||
                'Failed to set up 2FA'
        };
    }
}

export const verify2FASetup = async (data: { secret: string; token: string }) => {
    try {
        const response = await axios.post(
            `${config.server}/main/verify-2fa-setup`,
            data,
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        return { success: true, ...response.data };
    } catch (error: unknown) {
        const responseError = error as {
            response?: {
                data?: {
                    error?: string;
                }
            }
        };

        return {
            success: false,
            error: (responseError && 'response' in responseError && responseError.response?.data?.error) ||
                'Failed to verify 2FA setup'
        };
    }
}

export const disable2FA = async (token: string) => {
    try {
        const response = await axios.post(
            `${config.server}/main/disable-2fa`,
            { token },
            {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        );

        return { success: true, ...response.data };
    } catch (error: unknown) {
        const responseError = error as {
            response?: {
                data?: {
                    error?: string;
                }
            }
        };

        return {
            success: false,
            error: (responseError && 'response' in responseError && responseError.response?.data?.error) ||
                'Failed to disable 2FA'
        };
    }
}
